import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DISTRICTS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { MapPicker } from "@/components/MapPicker";
import { parseMapsUrl, rankAgents } from "@/lib/map";

const EMPTY = { name: "", phone: "", area: "", address: "", maps_url: "", lat: null, lng: null };

const Field = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

export const InspectionFormDialog = ({ open, onClose, onSaved, initial }) => {
  const { branches } = useAuth();
  const [agents, setAgents] = useState([]);
  const [customer, setCustomer] = useState(EMPTY);
  const [agentId, setAgentId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/agents").then((r) => setAgents(r.data.filter((a) => a.active))).catch(() => setAgents([]));
    setCustomer({ ...EMPTY, ...(initial?.customer || {}) });
    setAgentId(initial?.agent_id || "");
    setScheduledAt(initial?.scheduled_at || "");
    setNotes(initial?.admin_notes || "");
  }, [open, initial]);

  const sorted = rankAgents(agents, customer);

  const set = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agentId) return toast.error("اختر المندوب");
    if (!customer.branch) return toast.error("اختر الفرع");
    setBusy(true);
    const body = { customer, agent_id: agentId, scheduled_at: scheduledAt, admin_notes: notes };
    try {
      const { data } = initial ? await api.put(`/inspections/${initial.id}`, body) : await api.post("/inspections", body);
      toast.success(initial ? "تم حفظ التعديلات" : `تم إسناد المعاينة ${data.ref}`);
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="inspection-form-dialog">
        <DialogHeader><DialogTitle className="text-start font-heading">{initial ? "تعديل المعاينة" : "إسناد معاينة جديدة"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="اسم العميل *"><Input required value={customer.name} onChange={set("name")} data-testid="form-customer-name" /></Field>
            <Field label="رقم الهاتف *"><Input required dir="ltr" className="text-left" value={customer.phone} onChange={set("phone")} data-testid="form-customer-phone" /></Field>
            <Field label="الفرع *">
              <Select value={customer.branch || ""} onValueChange={(v) => setCustomer((c) => ({ ...c, branch: v, area: "" }))} dir="rtl">
                <SelectTrigger data-testid="form-branch-select"><SelectValue placeholder="اختر الفرع" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b} value={b} data-testid={`form-branch-${b}`}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="الحي">
              <Input list="district-options" value={customer.area} onChange={set("area")} placeholder={customer.branch ? "ابحث أو اختر الحي" : "اختر الفرع أولاً"} data-testid="form-customer-area" />
              <datalist id="district-options">{(DISTRICTS[customer.branch] || []).map((d) => <option key={d} value={d} />)}</datalist>
            </Field>
            <Field label="موعد الزيارة"><Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="form-scheduled-at" /></Field>
          </div>
          <Field label="العنوان التفصيلي"><Input value={customer.address} onChange={set("address")} data-testid="form-customer-address" /></Field>
          <Field label="رابط الموقع على Google Maps"><Input dir="ltr" className="text-left" placeholder="https://maps.google.com/..." value={customer.maps_url} onChange={(e) => { const v = e.target.value; setCustomer((c) => ({ ...c, maps_url: v, lat: null, lng: null, ...(parseMapsUrl(v) || {}) })); }} data-testid="form-maps-url" /></Field>
          <Field label={`تحديد الموقع على الخريطة (اضغط للتحديد)${customer.lat != null ? ` · ${customer.lat}, ${customer.lng}` : " · أو يُستخرج من رابط Google Maps تلقائياً"}`}>
            <MapPicker value={customer} onChange={(p) => setCustomer((c) => ({ ...c, ...p }))} />
          </Field>
          <Field label="المندوب * (مرتب حسب المسافة أو المنطقة ثم ضغط العمل)">
            <Select value={agentId} onValueChange={setAgentId} dir="rtl">
              <SelectTrigger data-testid="form-agent-select"><SelectValue placeholder="اختر المندوب" /></SelectTrigger>
              <SelectContent>
                {sorted.length === 0 && <div className="p-3 text-sm text-muted-foreground">أضف مندوباً أولاً من صفحة المناديب</div>}
                {sorted.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`form-agent-option-${a.id}`}>
                    {a.name} · {a.km != null ? `${a.km.toFixed(1)} كم` : a.region || "بدون منطقة"} · {a.active_tasks} مهام نشطة
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="ملاحظات للمندوب"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="form-admin-notes" /></Field>
          <button disabled={busy} data-testid="form-submit-button"
            className="w-full h-12 rounded-lg bg-brand-navy text-white font-bold hover:bg-brand-navy-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={18} className="animate-spin" />}{initial ? "حفظ" : "إسناد المعاينة"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
